import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { WorkTypeIcon } from '../components/KanbanBoard';
import { InlineField, RichContent, isHtml, getEffectiveLayout, LABEL_CLS } from '../components/FieldUtils';
import TagsField from '../components/TagsField';
import TimeBlock from '../components/TimeBlock';
import MentionTextarea, { MentionContent } from '../components/MentionTextarea';
import RichEditor, { RichEditorContent } from '../components/RichEditor';
import { useImagePaste } from '../hooks/useImagePaste';
import PastedImagePreview, { removePastedImageMarkdown } from '../components/PastedImagePreview';
import CommentAttachments from '../components/CommentAttachments';
import CommentReactions from '../components/CommentReactions';
import DescriptionAttachments from '../components/DescriptionAttachments';
import { useConfirm } from '../components/ConfirmDialog';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import SubtasksSection from '../components/SubtasksSection';
import AttachmentsSection from '../components/AttachmentsSection';
import LinkedIssuesSection from '../components/LinkedIssuesSection';
import WatchButton from '../components/WatchButton';
import HistoryEntry from '../components/HistoryEntry';
import CopyTaskLink from '../components/CopyTaskLink';
import CiLinkPanel from '../components/serviceDesk/CiLinkPanel';
import {
  CaretLeft, Trash, Clock, Flag, User, Tag, Paperclip,
  ChatCircle, ClockCounterClockwise, PaperPlaneRight, ArrowRight, PencilSimple, X as XIcon, FloppyDisk, ImageSquare,
} from '@phosphor-icons/react';

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' }, { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' }, { value: 'done', label: 'Done' },
];
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' },
];
const STATUS_COLORS = {
  todo: 'bg-zinc-100 text-zinc-700 border border-zinc-200',
  in_progress: 'bg-blue-50 text-blue-700 border border-blue-200',
  review: 'bg-amber-50 text-amber-700 border border-amber-200',
  done: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
};
const STATUS_DOT = { todo: 'bg-zinc-400', in_progress: 'bg-blue-500', review: 'bg-amber-500', done: 'bg-emerald-500' };
const PRIORITY_DOT = { low: 'bg-zinc-400', medium: 'bg-amber-500', high: 'bg-orange-500', critical: 'bg-red-500' };
// Enriched option rows for the inline Select menus: a colored dot / avatar
// beside the label so Status / Priority / Assignee dropdowns read at a glance.
const dotNode = (cls, label) => (
  <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full shrink-0 ${cls || 'bg-violet-500'}`} />{label}</span>
);
const assigneeNode = (m) => (
  <span className="flex items-center gap-2">
    <Avatar className="h-5 w-5"><AvatarImage src={m.avatar} /><AvatarFallback className="bg-blue-100 text-blue-700 text-[9px] font-semibold">{m.name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
    <span className="truncate">{m.name}</span>
  </span>
);
const WT_LABELS = { task: 'Task', story: 'Story', bug: 'Bug' };
const STATUS_LABELS = { todo: 'To Do', in_progress: 'In Progress', review: 'Review', done: 'Done' };
const FIELD_LABELS_MAP = { status: 'Status', priority: 'Priority', assignee: 'Assignee', title: 'Title', work_type: 'Type' };

// Field renderer for builtin fields
function BField({ id, task, members, canEdit, updateField, onPaste, onFileUpload, sprints, uploadImage, customStatuses }) {
  const assignee = members?.find(m => m.user_id === task?.assignee_id);
  const reporter = members?.find(m => m.user_id === task?.reporter_id);
  // Merge the 4 built-in statuses with any custom board columns the
  // project's owner/admin/PM has added on the board. Otherwise tasks that
  // sit in a custom column (e.g. READY FOR RETEST) can't be moved to /
  // edited from the detail page — the dropdown wouldn't list those values.
  const statusOptions = [
    ...STATUS_OPTIONS.map(s => ({ ...s, node: dotNode(STATUS_DOT[s.value], s.label) })),
    ...(customStatuses || []).map(s => ({ value: s.id, label: s.label, node: dotNode('bg-violet-500', s.label) })),
  ];
  const priorityOptions = PRIORITY_OPTIONS.map(p => ({ ...p, node: dotNode(PRIORITY_DOT[p.value], p.label) }));
  const assigneeOptions = [
    { value: 'unassigned', label: 'Unassigned', node: <span className="flex items-center gap-2 text-zinc-500"><User size={14} /> Unassigned</span> },
    ...(members || []).map(m => ({ value: m.user_id, label: m.name, node: assigneeNode(m) })),
  ];
  const currentStatusLabel = statusOptions.find(s => s.value === task?.status)?.label || task?.status;
  switch (id) {
    case 'description': return <div><Label className={LABEL_CLS}>Description</Label><InlineField value={task?.description || ''} type="textarea" richText canEdit={canEdit} uploadImage={uploadImage} displayValue={<RichContent content={task?.description} />} onSave={v => updateField('description', v)} onPaste={onPaste} onFileUpload={onFileUpload} /><DescriptionAttachments html={task?.description} /></div>;
    case 'type': return <div><Label className={LABEL_CLS}>Type</Label><div className="text-[13px] text-zinc-700 px-2 py-1 -mx-2 flex items-center gap-1.5" data-testid="task-type-readonly"><WorkTypeIcon type={task?.work_type||'task'} size={15} /> {WT_LABELS[task?.work_type]||'Task'}</div></div>;
    case 'status': return <div><Label className={LABEL_CLS}>Status</Label><InlineField value={task?.status} canEdit={canEdit} options={statusOptions} displayValue={<Badge className={`${STATUS_COLORS[task?.status]||'bg-zinc-100 text-zinc-700'} text-[11px]`}>{currentStatusLabel}</Badge>} onSave={v => updateField('status', v)} /></div>;
    case 'priority': return <div><Label className={LABEL_CLS}>Priority</Label><InlineField value={task?.priority} canEdit={canEdit} options={priorityOptions} displayValue={<span className="flex items-center gap-1.5 text-[13px] text-zinc-800 font-medium"><span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[task?.priority]||'bg-zinc-400'}`} /> {PRIORITY_OPTIONS.find(p=>p.value===task?.priority)?.label}</span>} onSave={v => updateField('priority', v)} /></div>;
    case 'assignee': return <div><Label className={LABEL_CLS}>Assignee</Label><InlineField value={task?.assignee_id||'unassigned'} canEdit={canEdit} options={assigneeOptions} displayValue={assignee ? <span className="flex items-center gap-2"><Avatar className="h-6 w-6"><AvatarImage src={assignee.avatar}/><AvatarFallback className="bg-blue-100 text-blue-700 text-[10px]">{assignee.name?.charAt(0)}</AvatarFallback></Avatar><span className="font-medium">{assignee.name}</span></span> : <span className="text-zinc-400"><User size={14} className="inline mr-1"/>Unassigned</span>} onSave={v => updateField('assignee_id', v==='unassigned'?null:v)} /></div>;
    case 'due_date': return <div><Label className={LABEL_CLS}>Due Date</Label><InlineField value={task?.due_date||''} type="date" canEdit={canEdit} displayValue={<span className="flex items-center gap-1.5 text-[13px] text-zinc-800"><Clock size={14}/> {task?.due_date ? new Date(task.due_date).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}) : 'Not set'}</span>} onSave={v => updateField('due_date', v||null)} /></div>;
    case 'tags': return <div><Label className={LABEL_CLS}>Tags</Label><TagsField projectId={task?.project_id} value={task?.tags || []} canEdit={canEdit} onSave={v => updateField('tags', v)} /></div>;
    case 'reporter': return <div><Label className={LABEL_CLS}>Reporter</Label><div className="flex items-center gap-2 text-[13px] text-zinc-800">{reporter ? <><Avatar className="h-6 w-6"><AvatarImage src={reporter.avatar}/><AvatarFallback className="bg-zinc-200 text-zinc-600 text-[10px]">{reporter.name?.charAt(0)}</AvatarFallback></Avatar><span className="font-medium">{reporter.name}</span></> : 'Unknown'}</div></div>;
    case 'created': return <div><Label className={LABEL_CLS}>Created</Label><span className="text-[12px] text-zinc-500">{task?.created_at ? new Date(task.created_at).toLocaleString() : '-'}</span></div>;
    case 'sprint': {
      const cur = sprints?.find(s => s.sprint_id === task?.sprint_id);
      const openSprints = (sprints || []).filter(s => s.status !== 'completed');
      const options = [{ value: 'backlog', label: 'Backlog' }, ...openSprints.map(s => ({ value: s.sprint_id, label: s.name + (s.status === 'active' ? ' · Active' : '') }))];
      if (cur && cur.status === 'completed') options.push({ value: cur.sprint_id, label: cur.name + ' · Completed' });
      return <div><Label className={LABEL_CLS}>Sprint</Label><InlineField value={task?.sprint_id || 'backlog'} canEdit={canEdit} options={options} displayValue={cur ? <span className="flex items-center gap-1.5 text-[13px] text-zinc-800 font-medium">{cur.name}{cur.status === 'active' && <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 border uppercase">Active</Badge>}{cur.status === 'completed' && <Badge className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200 border uppercase">Completed</Badge>}</span> : <span className="text-zinc-400 text-[13px]">Backlog</span>} onSave={v => updateField('sprint_id', v === 'backlog' ? null : v)} /></div>;
    }
    default: return null;
  }
}

function CField({ field, task, canEdit, updateCF, onPaste, onFileUpload, uploadImage }) {
  const val = task?.custom_fields?.[field.field_id] || '';
  const opts = field.type === 'select' ? field.options.map(o => ({ value: o, label: o })) : null;
  // Long-form text fields (text, richtext) render as textareas with image-paste support.
  const isLongText = field.type === 'text' || field.type === 'richtext';
  return <div><Label className={LABEL_CLS}>{field.name}</Label>{isLongText ? <InlineField value={val} type="textarea" richText canEdit={canEdit} uploadImage={uploadImage} displayValue={<RichContent content={val} />} onSave={v => updateCF(field.field_id, v)} onPaste={onPaste} onFileUpload={onFileUpload} /> : <InlineField value={val} type={field.type==='number'?'number':field.type==='date'?'date':'text'} options={opts} canEdit={canEdit} onSave={v => updateCF(field.field_id, v)} />}</div>;
}

export default function TaskDetailPage() {
  // The detail page is mounted on TWO routes:
  //   /tasks/:taskId    — internal opaque id
  //   /browse/:taskKey  — Jira-style permalink (URL stays as the key)
  // `useParams` returns whichever the route matched. On a key route we
  // first resolve to the task document via /tasks/by-key, then use the
  // resulting task_id for all subsequent calls.
  const { taskId: routeTaskId, taskKey: routeTaskKey } = useParams();
  const navigate = useNavigate();
  const { currentOrg, getOrgHeaders, API, user, switchOrg } = useAuth();
  const confirm = useConfirm();
  const { handlePaste: imgPaste, handleFileUpload: imgFileUpload, uploadImage } = useImagePaste(getOrgHeaders);
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [allProjectTasks, setAllProjectTasks] = useState([]);

  const [taskError, setTaskError] = useState(null); // 'not_found' | 'forbidden' | 'error'
  const [taskLoading, setTaskLoading] = useState(true);

  // Once resolved, this is the canonical id we use for all sub-resource
  // fetches (comments, activities, updates). On the /tasks/:taskId route
  // it equals routeTaskId; on /browse/:taskKey it's filled after the
  // initial by-key resolve.
  const taskId = task?.task_id || routeTaskId;

  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  const fetchTask = useCallback(async () => {
    if (!currentOrg) return;
    setTaskLoading(true);
    try {
      // Choose the right endpoint based on which route matched. The by-key
      // path returns the same shape so downstream state stays unchanged.
      const url = routeTaskKey
        ? `${API}/tasks/by-key/${encodeURIComponent(routeTaskKey)}`
        : `${API}/tasks/${routeTaskId}`;
      const { data, headers: respHeaders } = await axios.get(url, h());
      // Defensive guard for half-deployed prod boxes: if the backend hasn't
      // been restarted, the by-key route doesn't exist and Express returns
      // its default HTML 404 with `text/html`. axios's success path then
      // hands us that HTML as `data`, which downstream code would try to
      // render as a task. Detect that and force the not_found branch.
      const ct = (respHeaders?.['content-type'] || '').toLowerCase();
      if (typeof data === 'string' || ct.includes('text/html')) {
        setTaskError('deploy_mismatch');
        return;
      }
      setTask(data);
      setTaskError(null);
    } catch (err) {
      const status = err?.response?.status;
      const ct = (err?.response?.headers?.['content-type'] || '').toLowerCase();
      // Same defensive guard for the error path — some clients hit Express's
      // HTML 404 as an error.
      if (routeTaskKey && (ct.includes('text/html') || typeof err?.response?.data === 'string')) {
        setTaskError('deploy_mismatch');
        return;
      }
      // Cross-workspace fallback for /browse/<KEY>: the task key may live in
      // a different workspace the user also belongs to. Ask the backend to
      // resolve across every membership; if it finds the task, switch to
      // that workspace silently and re-fetch — the user lands on the right
      // task instead of staring at a "not found" screen.
      if (status === 404 && routeTaskKey && switchOrg) {
        try {
          const { data: resolved, headers: rh } = await axios.get(
            `${API}/tasks/resolve-key/${encodeURIComponent(routeTaskKey)}`,
            { withCredentials: true }
          );
          const rct = (rh?.['content-type'] || '').toLowerCase();
          if (typeof resolved === 'string' || rct.includes('text/html')) {
            setTaskError('deploy_mismatch');
            return;
          }
          if (resolved?.task && resolved?.org_id && resolved.org_id !== currentOrg?.org_id) {
            await switchOrg(resolved.org_id);
            setTask(resolved.task);
            setTaskError(null);
            setTaskLoading(false);
            if (resolved.org_name) toast.success(`Switched to "${resolved.org_name}" workspace`);
            return;
          }
          if (resolved?.task) {
            setTask(resolved.task);
            setTaskError(null);
            setTaskLoading(false);
            return;
          }
        } catch (resolveErr) {
          const rs = resolveErr?.response?.status;
          const rct2 = (resolveErr?.response?.headers?.['content-type'] || '').toLowerCase();
          if (rct2.includes('text/html') || typeof resolveErr?.response?.data === 'string') {
            setTaskError('deploy_mismatch');
            return;
          }
          if (rs === 403) { setTaskError('forbidden'); setTaskLoading(false); return; }
          // fall through to 404
        }
      }
      if (status === 404) setTaskError('not_found');
      else if (status === 403) setTaskError('forbidden');
      else setTaskError('error');
    } finally { setTaskLoading(false); }
  }, [routeTaskId, routeTaskKey, currentOrg, API, h, switchOrg]);
  const fetchMembers = useCallback(async () => { if (!currentOrg) return; try { const { data } = await axios.get(`${API}/members`, h()); setMembers(data); } catch {} try { const { data } = await axios.get(`${API}/members/directory`, h()); setDirectory(data); } catch {} }, [currentOrg, API, h]);
  const fetchComments = useCallback(async () => { if (!currentOrg || !taskId) return; try { const { data } = await axios.get(`${API}/tasks/${taskId}/comments`, h()); setComments([...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))); } catch {} }, [taskId, currentOrg, API, h]);
  const fetchActivities = useCallback(async () => { if (!currentOrg || !taskId) return; try { const { data } = await axios.get(`${API}/tasks/${taskId}/activities`, h()); setActivities(data); } catch {} }, [taskId, currentOrg, API, h]);

  useEffect(() => { fetchTask(); fetchMembers(); }, [fetchTask, fetchMembers]);
  // Comments + activities depend on the resolved task_id, so kick them off
  // only once the task has been loaded (after by-key resolve on /browse/).
  useEffect(() => { if (taskId) { fetchComments(); fetchActivities(); } }, [taskId, fetchComments, fetchActivities]);
  // Tell the sidebar which project this page belongs to so it can light up
  // the matching row in the Projects list. Listener: Sidebar.js.
  useEffect(() => {
    if (task?.project_id) {
      window.dispatchEvent(new CustomEvent('task-context', { detail: { project_id: task.project_id } }));
    }
    return () => {
      // Clear the highlight when navigating away from the task page.
      window.dispatchEvent(new CustomEvent('task-context', { detail: { project_id: null } }));
    };
  }, [task?.project_id]);
  useEffect(() => { if (task?.project_id && currentOrg) { axios.get(`${API}/projects/${task.project_id}`, h()).then(r => setProject(r.data)).catch(()=>{}); axios.get(`${API}/projects/${task.project_id}/members`, h()).then(r => setProjectMembers(r.data)).catch(()=>{}); axios.get(`${API}/projects/${task.project_id}/sprints`, h()).then(r => setSprints(r.data?.sprints || [])).catch(()=>{}); axios.get(`${API}/projects/${task.project_id}/tasks`, h()).then(r => setAllProjectTasks(r.data || [])).catch(()=>{}); } }, [task?.project_id, currentOrg]);

  const updateField = async (field, value) => { try { await axios.put(`${API}/tasks/${taskId}`, { [field]: value }, h()); fetchTask(); fetchActivities(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); } };
  const updateFields = async (patch) => { try { await axios.put(`${API}/tasks/${taskId}`, patch, h()); fetchTask(); fetchActivities(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); } };
  const updateCF = async (fieldId, value) => { const cf = { ...(task?.custom_fields || {}), [fieldId]: value }; try { await axios.put(`${API}/tasks/${taskId}`, { custom_fields: cf }, h()); fetchTask(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); } };
  const handlePostComment = async () => { if (!newComment.trim()) return; setPostingComment(true); try { await axios.post(`${API}/tasks/${taskId}/comments`, { content: newComment.trim() }, h()); setNewComment(''); fetchComments(); fetchActivities(); } catch {} finally { setPostingComment(false); } };
  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    try {
      await axios.put(`${API}/comments/${commentId}`, { content: editCommentText.trim() }, h());
      setEditingCommentId(null);
      fetchComments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save comment');
    }
  };
  const handleDeleteComment = async (commentId) => {
    if (!(await confirm({ title: 'Delete comment?', description: 'This cannot be undone.', confirmText: 'Delete', variant: 'destructive' }))) return;
    try {
      await axios.delete(`${API}/comments/${commentId}`, h());
      fetchComments();
      fetchActivities();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete comment');
    }
  };
  const handleDelete = async () => {
    if (!(await confirm({ title: `Delete ${task?.task_key}?`, description: 'This task and all its comments, attachments, and activity will be permanently removed.', confirmText: 'Delete task', variant: 'destructive' }))) return;
    try { await axios.delete(`${API}/tasks/${taskId}`, h()); navigate(-1); } catch {}
  };

  const resolveUser = (uid) => members.find(m => m.user_id === uid) || directory.find(m => m.user_id === uid);
  const getMemberName = (uid) => resolveUser(uid)?.name || 'Unknown';
  const getMemberAvatar = (uid) => resolveUser(uid)?.avatar || null;
  const timeAgo = (ds) => { const d = Date.now() - new Date(ds).getTime(); if (d < 60000) return 'just now'; if (d < 3600000) return `${Math.floor(d / 60000)}m`; if (d < 86400000) return `${Math.floor(d / 3600000)}h`; return new Date(ds).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
  const formatActivity = (act) => {
    const name = getMemberName(act.user_id);
    if (act.action === 'created') return <><strong>{name}</strong> created this</>;
    if (act.action === 'commented') return <><strong>{name}</strong> commented</>;
    if (act.action === 'updated' && act.details) { const fl = FIELD_LABELS_MAP[act.details.field] || act.details.field; const from = act.details.field === 'status' ? (STATUS_LABELS[act.details.from]||act.details.from) : act.details.from; const to = act.details.field === 'status' ? (STATUS_LABELS[act.details.to]||act.details.to) : act.details.to; return <><strong>{name}</strong> changed {fl}: {from||'none'} <ArrowRight size={9} className="inline mx-0.5" /> {to}</>; }
    return <><strong>{name}</strong> {act.action}</>;
  };

  const canEdit = currentOrg?.role !== 'viewer';
  if (!task) {
    if (taskLoading) {
      return <div className="flex-1 flex items-center justify-center"><div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;
    }
    // Error state — task fetch finished but returned no data (404/403/network error).
    const msgs = {
      not_found: routeTaskKey
        ? { title: `Task ${routeTaskKey} not found`, body: "This task key doesn't exist in any workspace you belong to. If a teammate sent you this link, ask them which workspace it's in and request access before clicking the link." }
        : { title: 'Task not found', body: "This task doesn't exist or it belongs to a different workspace. Try switching workspaces from the sidebar." },
      forbidden: { title: 'No access to this task', body: "This task lives in a project you're not a member of. Ask a project admin to add you, or pick a different task." },
      deploy_mismatch: {
        title: 'Backend out of date',
        body: "The server is running an older build that doesn't recognise this link format. Ask your admin to restart the backend service on this environment — the deploy didn't finish."
      },
      error: { title: 'Could not load task', body: "Something went wrong. Please try again or check your connection." },
    };
    const m = msgs[taskError] || msgs.error;
    return (
      <div className="flex-1 flex items-center justify-center p-8" data-testid="task-detail-error">
        <div className="max-w-md w-full text-center bg-white rounded-2xl border border-zinc-200 p-8 shadow-sm">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-zinc-100 flex items-center justify-center mb-3">
            <span className="text-2xl">🔍</span>
          </div>
          <h2 className="text-lg font-bold text-zinc-900">{m.title}</h2>
          <p className="text-sm text-zinc-500 mt-2">{m.body}</p>
          <div className="flex justify-center gap-2 mt-5">
            <button onClick={() => navigate(-1)} className="px-4 h-9 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">Go back</button>
            <button onClick={() => navigate('/')} className="px-4 h-9 rounded-lg bg-zinc-900 text-white text-sm hover:bg-zinc-800">Home</button>
          </div>
        </div>
      </div>
    );
  }

  // Layout
  const customFields = (project?.custom_fields || []).filter(cf => !cf.work_types?.length || cf.work_types.includes(task.work_type || 'task'));
  const useSprints = sprints.length > 0 || !!task?.sprint_id;
  const layout = getEffectiveLayout(project?.field_layouts, task.work_type || 'task', project?.custom_fields, project, useSprints);
  const builtinIds = ['description', 'type', 'status', 'priority', 'assignee', 'sprint', 'due_date', 'tags', 'reporter', 'created'];

  const renderField = (fId, zone) => {
    if (fId === 'subtasks') {
      if (task.work_type === 'task' || task.parent_task_id) return null;
      return (
        <SubtasksSection key="subtasks"
          parentTask={task} projectId={task.project_id} allTasks={allProjectTasks} members={members}
          onCreated={() => { axios.get(`${API}/projects/${task.project_id}/tasks`, h()).then(r => setAllProjectTasks(r.data || [])).catch(() => {}); }}
          onTaskClick={(t) => navigate(`/tasks/${t.task_id}`)} canEdit={canEdit} />
      );
    }
    if (fId === 'linked_issues') {
      return <LinkedIssuesSection key="linked_issues" task={task} projectId={task.project_id} allTasks={allProjectTasks} canEdit={canEdit} />;
    }
    if (builtinIds.includes(fId)) return <BField key={fId} id={fId} task={task} members={members} canEdit={canEdit} updateField={updateField} onPaste={imgPaste} onFileUpload={imgFileUpload} sprints={sprints} uploadImage={uploadImage} customStatuses={project?.custom_statuses} />;
    const cf = customFields.find(f => f.field_id === fId);
    if (cf) return <CField key={fId} field={cf} task={task} canEdit={canEdit} updateCF={updateCF} onPaste={imgPaste} onFileUpload={imgFileUpload} uploadImage={uploadImage} />;
    return null;
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="task-detail-page">
      <div className="h-12 flex items-center gap-3 px-6 border-b border-zinc-200 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-800 transition-colors" data-testid="back-button"><CaretLeft size={14} /> Back</button>
        <Separator orientation="vertical" className="h-5" />
        <WorkTypeIcon type={task.work_type || 'task'} size={16} />
        <span className="text-[12px] font-mono text-zinc-500 font-semibold sr-only">{task.task_key}</span>
        <CopyTaskLink taskKey={task.task_key} />
        <Badge className="text-[10px] bg-zinc-100 text-zinc-600 border border-zinc-200 font-semibold">{WT_LABELS[task.work_type] || 'Task'}</Badge>
        <div className="flex-1" />
        {canEdit && <Button variant="ghost" size="sm" onClick={handleDelete} className="h-7 text-[11px] text-red-400 hover:text-red-600" data-testid="delete-task-btn"><Trash size={13} className="mr-1" /> Delete</Button>}
        {task && <WatchButton taskId={task.task_id} />}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Main content */}
        <div className="flex-1 min-w-0 overflow-y-auto px-10 py-8">
          <div className="max-w-[960px] mx-auto">
            <InlineField value={task.title} canEdit={canEdit} maxLength={255} displayValue={<h1 className="font-['Outfit'] text-[22px] font-bold text-zinc-900 tracking-tight">{task.title}</h1>} onSave={v => updateField('title', v)} />

          {/* Main zone fields from layout (incl. Sub-tasks & Linked Items when configured) */}
          <div className="mt-6 space-y-6">
            {layout.main.map(fId => renderField(fId, 'main'))}
          </div>

          {/* Attachments — files attached to this task (PDFs, screenshots, designs). */}
          <div className="mt-6">
            <AttachmentsSection
              taskId={task.task_id}
              canEdit={canEdit}
              currentUserId={user?.user_id}
            />
          </div>

          <Separator className="my-8" />

          <Tabs defaultValue="comments">
            <TabsList className="h-9 bg-transparent border-b border-zinc-100 rounded-none justify-start gap-0 p-0 w-full">
              <TabsTrigger value="comments" className="text-[12px] font-semibold rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-3 py-2" data-testid="comments-tab-full">
                <ChatCircle size={13} className="mr-1" /> Comments {comments.length > 0 && `(${comments.length})`}
              </TabsTrigger>
              <TabsTrigger value="activity" className="text-[12px] font-semibold rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-3 py-2" data-testid="activity-tab-full">
                <ClockCounterClockwise size={13} className="mr-1" /> History
              </TabsTrigger>
            </TabsList>
            <TabsContent value="comments" className="mt-4 space-y-4">
              {canEdit && (
                <div className="mb-4">
                  <RichEditor value={newComment} onChange={setNewComment}
                    uploadImage={uploadImage}
                    mentionItems={projectMembers}
                    placeholder="Write a comment... Type @ to mention a teammate."
                    minHeight={80} />
                  <div className="flex items-center justify-end mt-2">
                    <Button onClick={handlePostComment} disabled={postingComment || !newComment.trim()} size="sm" className="h-8 px-4 bg-blue-600 hover:bg-blue-700 text-white text-[11px]" data-testid="post-comment-btn-full"><PaperPlaneRight size={14} className="mr-1" /> Post Comment</Button>
                  </div>
                </div>
              )}
              {comments.length === 0 && <p className="text-[13px] text-zinc-400 text-center py-6">No comments yet.</p>}
              {comments.map(c => {
                const cu = resolveUser(c.user_id);
                // Imported Jira comments carry the original author in
                // `imported_author_name`. We surface that as an "Imported from
                // Jira · <author>" attribution line inside the comment body so
                // it reads clearly in every view (full page + slide-over),
                // regardless of who ran the import. Older imports baked the
                // same header into the HTML body, so we only add the line when
                // the structured field is present (no duplication).
                const jiraAuthor = c.imported_from === 'jira' && c.imported_author_name ? c.imported_author_name : null;
                const isOwn = c.user_id === user?.user_id || ['owner','admin'].includes(currentOrg?.role);
                const isEditing = editingCommentId === c.comment_id;
                return (
                  <div key={c.comment_id} className="flex gap-3 group" data-testid={`comment-${c.comment_id}`}>
                    <Avatar className="h-7 w-7 flex-shrink-0 mt-0.5"><AvatarImage src={cu?.avatar}/><AvatarFallback className="bg-gradient-to-br from-blue-400 to-indigo-500 text-white text-[9px] font-bold">{(cu?.name||'?').charAt(0).toUpperCase()}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[13px] font-bold text-zinc-800">{cu?.name || 'Unknown'}</span>
                        <span className="text-[10px] text-zinc-400">{timeAgo(c.created_at)}</span>
                        {c.updated_at && c.updated_at !== c.created_at && <span className="text-[9px] text-zinc-400 italic">(edited)</span>}
                        {isOwn && !isEditing && (
                          <div className="flex items-center gap-1 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setEditingCommentId(c.comment_id); setEditCommentText(c.content); }} className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-400 hover:text-blue-600 transition-colors" title="Edit" data-testid={`edit-comment-${c.comment_id}`}>
                              <PencilSimple size={13} />
                            </button>
                            <button onClick={() => handleDeleteComment(c.comment_id)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-400 hover:text-red-500 transition-colors" title="Delete" data-testid={`delete-comment-${c.comment_id}`}>
                              <Trash size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="space-y-2">
                          <RichEditor value={editCommentText} onChange={setEditCommentText}
                            uploadImage={uploadImage} minHeight={80}
                            mentionItems={projectMembers}
                            placeholder="Edit your comment..." autoFocus />
                          <div className="flex items-center justify-end gap-1.5">
                            <Button variant="ghost" size="sm" onClick={() => setEditingCommentId(null)} className="h-6 px-2 text-[10px] text-zinc-500" data-testid={`cancel-edit-comment-${c.comment_id}`}><XIcon size={10} className="mr-0.5" /> Cancel</Button>
                            <Button size="sm" onClick={() => handleEditComment(c.comment_id)} disabled={!editCommentText.trim()} className="h-6 px-2 bg-blue-600 hover:bg-blue-700 text-white text-[10px]" data-testid={`save-edit-comment-${c.comment_id}`}><FloppyDisk size={10} className="mr-0.5" /> Save</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="text-[13px] text-zinc-700 leading-relaxed bg-zinc-50 rounded-lg px-3 py-2 border border-zinc-100 min-w-0 [overflow-wrap:anywhere]">
                            {jiraAuthor && <p className="text-[11px] text-zinc-500 mb-1.5" data-testid={`comment-jira-author-${c.comment_id}`}>Imported from Jira · <strong className="text-zinc-700">{jiraAuthor}</strong></p>}
                            {isHtml(c.content)
                              ? <RichEditorContent html={c.content} />
                              : <MentionContent content={c.content} currentUserId={user?.user_id} />}
                          </div>
                          {!isHtml(c.content) && <CommentAttachments content={c.content} />}
                          <CommentReactions comment={c} userId={user?.user_id} headers={getOrgHeaders()}
                            onUpdated={(updated) => setComments(prev => prev.map(cm => cm.comment_id === updated.comment_id ? updated : cm))} />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </TabsContent>
            <TabsContent value="activity" className="mt-4 divide-y divide-zinc-100" data-testid="history-list-full">
              {activities.length === 0 && <p className="text-[13px] text-zinc-400 text-center py-6">No history yet. Field changes will appear here as they happen.</p>}
              {activities.map(act => (
                <HistoryEntry key={act.activity_id} activity={act} members={members}
                  getMemberAvatar={getMemberAvatar} getMemberName={getMemberName} />
              ))}
            </TabsContent>
          </Tabs>
          </div>
        </div>

        {/* Right sidebar: Fields from layout */}
        <div className="w-[300px] border-l border-zinc-200 overflow-y-auto p-6 space-y-5 bg-zinc-50/50 flex-shrink-0">
          <h3 className="text-[11px] font-bold text-zinc-600 uppercase tracking-[0.12em] mb-4">Details</h3>
          {layout.sidebar.map(fId => renderField(fId, 'sidebar'))}
          <div className="pt-4 border-t border-zinc-200"><CiLinkPanel targetType="task" targetId={task.task_id} /></div>
          <TimeBlock task={task} project={project} canEdit={canEdit} onSave={updateFields} />
        </div>
      </div>
    </div>
  );
}
